import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const infoConfig = [
  { key: 'address', icon: MapPin },
  { key: 'phone', icon: Phone },
  { key: 'email', icon: Mail },
  { key: 'hours', icon: Clock },
];

export const Contact = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    gradeLevel: '',
    source: '',
    housing: false,
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.source) {
      toast.error(t('contact.form.sourceRequired'));
      return;
    }
    setIsSubmitting(true);

    try {
      const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Nouvelle demande site Lafayette — ${formData.parentName}`,
        from_name: 'Site Lafayette International School',
        to: 'collegelyceelafayette@gmail.com',
        'Nom du parent': formData.parentName,
        'Email': formData.email,
        'Téléphone': formData.phone || 'Non renseigné',
        'Niveau scolaire': formData.gradeLevel || 'Non renseigné',
        'Comment nous avez-vous connus': formData.source || 'Non renseigné',
        'Hébergement souhaité': formData.housing ? 'Oui' : 'Non',
        'Message': formData.message || 'Aucun message',
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        toast.success(t('contact.form.toastSuccess'));
        setTimeout(() => {
          setFormData({ parentName: '', email: '', phone: '', gradeLevel: '', source: '', housing: false, message: '' });
          setIsSubmitted(false);
        }, 3000);
      } else {
        toast.error(t('contact.form.toastError'));
      }
    } catch (err) {
      toast.error(t('contact.form.toastNetwork'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" data-testid="contact-section" ref={ref} className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('contact.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('contact.title1')} <span className="italic">{t('contact.title1Italic')}</span>
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-slate-900 rounded-3xl p-8 h-full">
              <h3 className="font-playfair text-2xl font-bold text-white mb-8">
                {t('contact.infoTitle')}
              </h3>

              <div className="space-y-6">
                {infoConfig.map((item) => (
                  <div
                    key={item.key}
                    data-testid={`contact-info-${item.key}`}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {t(`contact.info.${item.key}.content`)}
                      </div>
                      <div className="text-slate-400 text-sm">
                        {t(`contact.info.${item.key}.sub`)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                data-testid="contact-map"
                className="mt-8 rounded-2xl overflow-hidden border border-slate-800 shadow-lg"
              >
                <iframe
                  title="Lafayette International School - Localisation"
                  src="https://www.google.com/maps?q=10%20Avenue%20Georges%20Clemenceau%2006000%20Nice&output=embed"
                  width="100%"
                  height="240"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=10+Avenue+Georges+Clemenceau+06000+Nice"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-link"
                className="mt-3 inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors"
              >
                <MapPin className="w-4 h-4" />
                {t('contact.directions')}
              </a>

            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-slate-50 rounded-3xl p-8"
            >
              <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-6">
                {t('contact.formTitle')}
              </h3>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="font-playfair text-xl font-bold text-slate-900 mb-2">
                    {t('contact.form.successTitle')}
                  </h4>
                  <p className="text-slate-600">{t('contact.form.successText')}</p>
                </motion.div>
              ) : (
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="parentName" className="font-inter text-slate-700">
                        {t('contact.form.parentName')}
                      </Label>
                      <Input
                        id="parentName"
                        name="parentName"
                        data-testid="input-parent-name"
                        value={formData.parentName}
                        onChange={handleChange}
                        required
                        className="input-focus border-slate-200 focus:border-amber-400"
                        placeholder={t('contact.form.parentNamePh')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-inter text-slate-700">
                        {t('contact.form.email')}
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-focus border-slate-200 focus:border-amber-400"
                        placeholder={t('contact.form.emailPh')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-inter text-slate-700">
                      {t('contact.form.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-focus border-slate-200 focus:border-amber-400"
                      placeholder={t('contact.form.phonePh')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gradeLevel" className="font-inter text-slate-700">
                      {t('contact.form.grade')}
                    </Label>
                    <Select
                      value={formData.gradeLevel}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, gradeLevel: value }))}
                    >
                      <SelectTrigger
                        data-testid="select-grade-level"
                        className="border-slate-200 focus:border-amber-400"
                      >
                        <SelectValue placeholder={t('contact.form.gradePh')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6eme">6ème (11-12 years)</SelectItem>
                        <SelectItem value="5eme">5ème (12-13 years)</SelectItem>
                        <SelectItem value="4eme">4ème (13-14 years)</SelectItem>
                        <SelectItem value="3eme">3ème (14-15 years)</SelectItem>
                        <SelectItem value="2nde">2nde (15-16 years)</SelectItem>
                        <SelectItem value="1ere">1ère (16-17 years)</SelectItem>
                        <SelectItem value="terminale">Terminale (17-18 years)</SelectItem>
                        <SelectItem value="autre">{t('contact.form.gradeOther')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="source" className="font-inter text-slate-700">
                      {t('contact.form.source')}
                    </Label>
                    <Select
                      value={formData.source}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, source: value }))}
                      required
                    >
                      <SelectTrigger
                        data-testid="select-source"
                        className="border-slate-200 focus:border-amber-400"
                      >
                        <SelectValue placeholder={t('contact.form.sourcePh')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">{t('contact.form.sourceOptions.web')}</SelectItem>
                        <SelectItem value="recreanice">{t('contact.form.sourceOptions.recreanice')}</SelectItem>
                        <SelectItem value="bouche-oreille">{t('contact.form.sourceOptions.wordOfMouth')}</SelectItem>
                        <SelectItem value="eibschools">{t('contact.form.sourceOptions.eibschools')}</SelectItem>
                        <SelectItem value="autres">{t('contact.form.sourceOptions.other')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50/60 border border-amber-100">
                    <Checkbox
                      id="housing"
                      data-testid="checkbox-housing"
                      checked={formData.housing}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, housing: checked === true }))
                      }
                      className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="housing"
                        className="font-inter text-slate-800 font-medium cursor-pointer"
                      >
                        {t('contact.form.housingLabel')}
                      </Label>
                      <p className="font-inter text-xs text-slate-500 mt-1">
                        {t('contact.form.housingHint')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-inter text-slate-700">
                      {t('contact.form.message')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="input-focus border-slate-200 focus:border-amber-400 resize-none"
                      placeholder={t('contact.form.messagePh')}
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-inquiry-btn"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg font-semibold btn-shine"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('contact.form.submitting')}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        {t('contact.form.submit')}
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
